import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, X, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useDeleteProduct, checkPendingOrders } from '../../services/adminService';

const overlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modal = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', damping: 25, stiffness: 300 } },
  exit: { opacity: 0, scale: 0.95, y: 10, transition: { duration: 0.15 } },
};

/**
 * Confirmation modal for deleting a product.
 * Requires typing the product name to confirm.
 * Warns if the product has pending orders.
 *
 * @param {{ product: object, onClose: () => void, onDeleted: () => void }} props
 */
export function DeleteProductModal({ product, onClose, onDeleted }) {
  const deleteMutation = useDeleteProduct();
  const [confirmText, setConfirmText] = useState('');
  const [hasPendingOrders, setHasPendingOrders] = useState(false);
  const [checking, setChecking] = useState(true);

  const confirmed = confirmText.toLowerCase() === (product?.title || '').toLowerCase();

  // Check for pending orders on mount
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    setChecking(true);
    checkPendingOrders(product.id).then((pending) => {
      if (!cancelled) {
        setHasPendingOrders(pending);
        setChecking(false);
      }
    });
    return () => { cancelled = true; };
  }, [product]);

  // Escape to close
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDelete = () => {
    if (!confirmed) return;
    deleteMutation.mutate(product.id, {
      onSuccess: () => {
        onDeleted?.();
        onClose();
      },
    });
  };

  if (!product) return null;

  return (
    <AnimatePresence>
      {product && (
        <>
          <motion.div
            key="delete-backdrop"
            variants={overlay}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm"
          />

          <motion.div
            key="delete-modal"
            variants={modal}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className="bg-brand-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <Trash2 size={20} className="text-error" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-brand-dark">Delete Product</h3>
                    <p className="text-xs text-brand-gray">This action cannot be easily undone</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                {/* Pending orders warning */}
                {checking ? (
                  <div className="flex items-center gap-2 text-sm text-brand-gray">
                    <Loader2 size={14} className="animate-spin" />
                    Checking for pending orders...
                  </div>
                ) : hasPendingOrders ? (
                  <div className="flex items-start gap-3 p-3 bg-warning/10 border border-warning/20 rounded-xl">
                    <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-bold text-brand-dark">Pending orders exist</p>
                      <p className="text-brand-gray mt-0.5">
                        This product has pending orders. Deleting it will archive it but order
                        history will be preserved.
                      </p>
                    </div>
                  </div>
                ) : null}

                <p className="text-sm text-brand-gray leading-relaxed">
                  You are about to delete{' '}
                  <span className="font-bold text-brand-dark">{product.title}</span>. The product
                  will be archived and hidden from the menu.
                </p>

                {/* Type to confirm */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    Type <span className="text-error">"{product.title}"</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={product.title}
                    autoFocus
                    className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-error/20 placeholder:text-ui-muted"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-ui-border flex items-center gap-3 bg-ui-background">
                <button
                  onClick={handleDelete}
                  disabled={!confirmed || deleteMutation.isPending}
                  className={clsx(
                    'flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl transition-all',
                    confirmed && !deleteMutation.isPending
                      ? 'bg-error hover:bg-red-700 text-brand-white shadow-lg'
                      : 'bg-gray-200 text-brand-gray cursor-not-allowed'
                  )}
                >
                  {deleteMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete Product'}
                </button>
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>

                {deleteMutation.isError && (
                  <p className="text-xs text-error ml-auto">
                    {deleteMutation.error?.message || 'Failed to delete'}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
